import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, HostListener } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  AlertService,
  CampaignService,
  FunnelService,
  StepEmulatorService,
  StorageService,
  StyleService,
} from '../../../_services';
import {
  createStepMediaInitialData,
  createStepResponseMediaInitialData,
  externalButtonUsage,
  getTruncatedProductName,
  initializeForm,
  mediaExcludedSteps,
  patchInputs,
  stepLayout,
} from '../../step-util';
import { Form } from '../../../_forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import {
  Campaign,
  CampaignProduct,
  defaultThemeColor,
  Funnel,
  FunnelStep,
  getCampaignProductImageUrl,
  getDiscountSettingFromStep,
  StepEmulatorDevices,
  StepStaticDataImageType,
  Style,
} from '../../../_models';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'offer-preview',
  templateUrl: './offer-preview.component.html',
  styleUrls: ['./offer-preview.component.scss'],
})
export class OfferPreviewComponent extends Form implements OnInit, OnChanges, OnDestroy {
  @Input() previewOffer: any;
  @Input() themes: Style[] = [];
  @Input() randomProduct: CampaignProduct = null;
  campaign: Campaign;
  activeDevice: StepEmulatorDevices = StepEmulatorDevices.Desktop;
  deviceType = StepEmulatorDevices;
  offer: any;
  isLoading: boolean = false;
  stepLoading: boolean = false;
  currentStepIndex: number = 0;
  currentStep: any = null;
  theme: string = defaultThemeColor;
  productPlaceholderUrl: string = '';
  stepDiscounts = {};
  selectedProduct: CampaignProduct = null;

  private stepForms = {};

  constructor(
    private modalService: NgxSmartModalService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected location: Location,
    protected funnelService: FunnelService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected sanitizer: DomSanitizer,
    protected stepEmulatorService: StepEmulatorService,
    protected campaignService: CampaignService,
    protected storageService: StorageService,
    protected styleService: StyleService
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.isLoading = true;
    this.campaign = this.campaignService.getCurrentCampaign();
    if (this.themes.length && this.campaign.styles.length) {
      const selectedTheme = this.themes.find((theme) => theme.id === this.campaign.styles[0]);
      if (selectedTheme && selectedTheme.variables && selectedTheme.variables.theme_color) {
        this.theme = selectedTheme.variables.theme_color;
      }
    }
    this.fetchOfferDetails();
    this.setProduct();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['previewOffer'] && !changes['previewOffer'].firstChange) {
      this.isLoading = true;
      this.randomProduct = null;
      this.selectedProduct = null;
      this.productPlaceholderUrl = '';
      this.currentStepIndex = 0;
      this.currentStep = null;
      this.offer = null;
      this.stepForms = {};
      this.theme = defaultThemeColor;
      this.campaign = this.campaignService.getCurrentCampaign();
      this.activeDevice = StepEmulatorDevices.Desktop;
      this.stepDiscounts = {};
      this.fetchOfferDetails();
      this.setProduct();
    }
  }

  fetchOfferDetails() {
    this.funnelService.get(this.previewOffer.id).subscribe(
      (offer: Funnel) => {
        this.offer = offer;
        if (this.offer && this.offer.offer_steps && this.offer.offer_steps.length > 0) {
          this.currentStep = this.offer.steps[0];
        }
        this.processOffer();
      },
      (error) => {
        this.handleError(error);
        this.isLoading = false;
      }
    );
  }

  getStepLayout(step: FunnelStep) {
    return stepLayout[step.category] || [];
  }

  getStepForm(step: FunnelStep) {
    return this.stepForms[step.id];
  }

  private processOffer() {
    this.offer.offer_steps.forEach((step) => {
      if (step) {
        const form = initializeForm(this.formBuilder, step.category, this.offer.offer_type, false);
        if (form) {
          if (!mediaExcludedSteps.includes(step.category)) {
            form.setControl(
              'static_data',
              this.formBuilder.group({
                image: null,
                image_tablet: null,
                image_mobile: null,
                embed_code: null,
                image_type: StepStaticDataImageType.None,
              })
            );
          }
          this.stepForms[step.id] = form;
        }
        step.useExternalButtons = externalButtonUsage(step.category);

        const stepMedia = { ...createStepMediaInitialData() };
        const stepResponseMedia = { ...createStepResponseMediaInitialData() };

        patchInputs(
          form,
          this.formBuilder,
          step,
          step.useExternalButtons,
          stepMedia,
          stepResponseMedia,
          this.sanitizer,
          this.stepEmulatorService
        );
      }
    });
    this.isLoading = false;
  }

  setProduct() {
    const product = this.storageService.get('selectedStepProduct');
    if (product && product.id) {
      setTimeout(() => {
        this.onProductSelected(product);
      }, 1000);
    } else {
      this.productPlaceholderUrl = getCampaignProductImageUrl(this.randomProduct);
    }
  }

  onProductSelected(product: CampaignProduct) {
    if (product) {
      this.selectedProduct = product;
      this.storageService.set('selectedStepProduct', product);
      this.updateProductImageUrl();
    }
    this.modalService.getModal('selectProductsDialog').close();
  }

  updateProductImageUrl() {
    this.productPlaceholderUrl = this.selectedProduct.id ? getCampaignProductImageUrl(this.selectedProduct) : null;
  }

  openProductSelector() {
    this.modalService.getModal('selectProductsDialog').open();
  }

  getProductName(): string {
    if (this.selectedProduct && this.selectedProduct.name) {
      return getTruncatedProductName(this.selectedProduct.name);
    } else if (this.randomProduct && this.randomProduct.name) {
      return getTruncatedProductName(this.randomProduct.name);
    }
  }

  getDiscountSettingFromStep(step: FunnelStep) {
    let discountSetting = null;

    if (step) {
      if (step.id && step.id in this.stepDiscounts) {
        discountSetting = this.stepDiscounts[step.id];
      } else {
        discountSetting = getDiscountSettingFromStep(step);

        if (step.id) {
          this.stepDiscounts[step.id] = discountSetting; // cache the discount setting
        }
      }
    }

    return discountSetting;
  }

  setActiveDevice(device: StepEmulatorDevices) {
    this.activeDevice = device;
  }

  isDeviceActive(device: StepEmulatorDevices): boolean {
    return this.activeDevice === device;
  }

  closeModal(): void {
    this.modalService.getModal('offerPreviewPopup').close();
  }

  navigateToPreviousStep() {
    if (this.currentStepIndex > 0 && !this.stepLoading) {
      this.stepLoading = true;

      setTimeout(() => {
        this.stepLoading = false;
        this.currentStepIndex--;
        this.currentStep = this.offer.offer_steps[this.currentStepIndex];
      }, 600);
    }
  }

  navigateToNextStep() {
    if (this.currentStepIndex < this.offer.offer_steps.length - 1 && !this.stepLoading) {
      this.stepLoading = true;

      setTimeout(() => {
        this.stepLoading = false;
        this.currentStepIndex++;
        this.currentStep = this.offer.offer_steps[this.currentStepIndex];
      }, 600);
    }
  }

  navigateToStep(): void {
    const url = this.router.createUrlTree(
      ['category', this.offer.offer_type, this.currentStep.category, 'step', this.currentStep.id, this.activeDevice],
      { relativeTo: this.route }
    );
    window.open(this.router.serializeUrl(url), '_blank');
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    this.closeModal();
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  handleRightArrow(event: KeyboardEvent) {
    this.navigateToNextStep();
  }

  @HostListener('document:keydown.arrowleft', ['$event'])
  handleLeftArrow(event: KeyboardEvent) {
    this.navigateToPreviousStep();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
